import { Component, OnInit } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { AuthService } from "../../insa-services";

import { appConfig } from "../../../insaweb.config";

type CertLoginSuccess = {
  token: string;
  serialNumber?: string;
  subject?: string;
};
type CertLoginError = { code?: string; message?: string };

@Component({
  templateUrl: "twoFactorCertificate.component.html",
})
export class TwoFactorCertificateComponent implements OnInit {
  username = "";
  errorMessage: string | null = null;
  subtitle = "";
  loginButtonLabel = "";
  // "login" (default), "reset" (forgot password) or "change" (Settings).
  // Captured once because clearPreTwoFactorState() wipes it.
  purpose: string | null = null;
  private readonly certBridgeBaseUrl = appConfig.certBridgeBaseUrl;

  get isChangeFlow(): boolean {
    return this.purpose === "change";
  }

  constructor(
    private readonly http: HttpClient,
    private readonly route: ActivatedRoute,
    private authService: AuthService,
    public router: Router,
  ) {}

  ngOnInit(): void {
    this.purpose = this.authService.getTwoFactorPurpose();

    const culture = localStorage.getItem("selected-locale") || (appConfig as any).defaultLocale || "sr-RS";
    this.subtitle = (appConfig as any).messages?.twoFactorCertSubtitle?.[culture]
      || "Priključite USB uređaj sa sertifikatom, zatim kliknite na Prijava.";
    this.loginButtonLabel = this.isChangeFlow
      ? "Potvrdi"
      : (appConfig as any).loginButtonLabel || "Prijava";
  }

  loginWithCertificate(): void {
    this.errorMessage = null;
    this.username = this.getPreAuthTwoFactorUsername() || "";
    if (!this.username) {
      this.errorMessage = "Username is required before certificate login.";
      console.error(this.errorMessage);
      return;
    }

    // The bridge calls TwoFactorController.cert-validate server-to-server and
    // has no preauth JWT, so the password-flow purpose must travel explicitly
    // (CertLoginRequestDto.Purpose). The bridge has to forward this query
    // param as "Purpose" in its cert-validate request body.
    let url =
      `${this.certBridgeBaseUrl}/api/auth/verify` +
      `?username=${encodeURIComponent(this.username)}`;
    if (this.purpose === "reset" || this.purpose === "change") {
      url += `&purpose=${encodeURIComponent(this.purpose)}`;
    }

    this.http.get<CertLoginSuccess>(url, { withCredentials: true }).subscribe({
      next: (res) => {
        // Branch on why the user is here. Certificate verification proves
        // identity either way; what differs is what we do with that proof.
        if (this.purpose === "reset") {
          this.authService.setResetToken(res.token);
          this.authService.clearPreTwoFactorState();
          this.router.navigate(["/resetPassword"]);
          return;
        }

        if (this.purpose === "change") {
          this.authService.setPasswordChangeToken(res.token);
          this.authService.clearPreTwoFactorState();
          this.router.navigate(["/changePasswordVerified"]);
          return;
        }

        this.authService.performAuthenticaton(res.token);
        this.authService.clearPreTwoFactorState();
        this.authService.emitLogedInStatusChange();
        this.router.navigate(["/"]);
      },
      error: (err: HttpErrorResponse) => {
        const { message, code } = this.extractBridgeError(err);
        this.errorMessage = code ? `${message} (${code})` : message;
        console.error("Certificate login failed", err);
      },
    });
  }

  // Only shown for the change flow: a logged-in user goes back into the app.
  cancel(): void {
    this.authService.clearPreTwoFactorState();
    this.router.navigate(["/"]);
  }

  getPreAuthTwoFactorUsername(): string | null {
    return localStorage.getItem("pre2fa_username");
  }
  private extractBridgeError(err: HttpErrorResponse): {
    message: string;
    code?: string;
  } {
    const body = err?.error as CertLoginError | string | null | undefined;
    if (typeof body === "string" && body.trim()) {
      return { message: body };
    }
    if (body && typeof body === "object") {
      const msg =
        typeof body.message === "string" && body.message ? body.message : null;
      const code =
        typeof body.code === "string" && body.code ? body.code : undefined;
      if (msg) return { message: msg };
    }
    return { message: err?.message || "Certificate login failed." };
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-login/twoFactorCertificate.component.ts